import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { Button } from './button';
import { Separator } from './separator';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
}

export function Modal({ open, onClose, title, children, footer, header, className }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        overlayRef.current === event.target
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = 'visible';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-[max(12px,env(safe-area-inset-top))_12px_max(12px,env(safe-area-inset-bottom))]'
    >
      <div
        ref={modalRef}
        className={cn(
          'relative flex max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl',
          'max-w-[95%] md:max-w-[798px]',
          className,
        )}
        role='dialog'
        aria-modal='true'
      >
        {header ? (
          header
        ) : (
          <div className='flex flex-row items-center justify-between pb-4 px-7 pt-7'>
            {title && <h2 className='text-[26px] font-bold'>{title}</h2>}
            <Button
              id='modal-close-btn'
              variant='ghost'
              size='icon'
              className='h-6 w-6 rounded-md'
              onClick={onClose}
            >
              <X className='h-6 w-6' />
              <span className='sr-only'>Close</span>
            </Button>
          </div>
        )}
        <div className='px-7'>
          <Separator className='bg-[#FFFFFF21]' />
        </div>
        <div className='flex-1 overflow-y-auto min-h-0'>
          {children}
        </div>
        {footer && (
          <div className='px-7'>
            <Separator className='bg-[#FFFFFF21]' />
          </div>
        )}
        {footer && <div>{footer}</div>}
      </div>
    </div>
  );
}
